/*
 * @Author: Zhouqi
 * @Date: 2025-11-12 19:28:42
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-01-05 18:04:55
 */
/**
 * 从文件名中获取扩展名（小写）
 */
export const getFileExtension = (filename: string): string => {
  if (!filename) return '';
  const match = filename.match(/\.([^.]+)$/);
  return match ? `.${match[1].toLowerCase()}` : '';
};

/**
 * 音频文件扩展名列表
 */
export const AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.m4a',
  '.aac',
  '.ogg',
  '.flac',
  '.wma',
  '.ape',
  '.aiff',
  '.opus',
];

/**
 * 视频文件扩展名列表
 */
export const VIDEO_EXTENSIONS = [
  '.mp4',
  '.mov',
  '.avi',
  '.wmv',
  '.flv',
  '.mkv',
  '.webm',
  '.m4v',
  '.mpg',
  '.mpeg',
  '.3gp',
  '.ts',
];

/**
 * 图片文件扩展名列表
 */
export const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.webp',
  '.svg',
  '.ico',
  '.tiff',
  '.tif',
];

/**
 * 判断文件是否为音频类型
 * 优先使用文件扩展名判断，其次使用 MIME type
 */
export const isAudioFile = (file: File): boolean => {
  if (!file || !file.name) return false;

  // 1. 优先通过文件扩展名判断
  const extension = getFileExtension(file.name);
  if (extension && AUDIO_EXTENSIONS.includes(extension)) {
    return true;
  }

  // 2. 通过 MIME type 判断（可能为空或不准确）
  if (file.type && typeof file.type === 'string' && file.type.startsWith('audio/')) {
    return true;
  }

  return false;
};

/**
 * 判断文件是否为视频类型
 * 优先使用文件扩展名判断，其次使用 MIME type
 */
export const isVideoFile = (file: File): boolean => {
  if (!file || !file.name) return false;

  // 1. 优先通过文件扩展名判断
  const extension = getFileExtension(file.name);
  if (extension && VIDEO_EXTENSIONS.includes(extension)) {
    return true;
  }

  // 2. 通过 MIME type 判断（可能为空或不准确）
  if (file.type && typeof file.type === 'string' && file.type.startsWith('video/')) {
    return true;
  }

  return false;
};

/**
 * 判断文件是否为图片类型
 * 优先使用文件扩展名判断，其次使用 MIME type
 */
export const isImageFile = (file: File): boolean => {
  if (!file || !file.name) return false;

  // 1. 优先通过文件扩展名判断
  const extension = getFileExtension(file.name);
  if (extension && IMAGE_EXTENSIONS.includes(extension)) {
    return true;
  }

  // 2. 通过 MIME type 判断（可能为空或不准确）
  if (file.type && typeof file.type === 'string' && file.type.startsWith('image/')) {
    return true;
  }

  return false;
};

/**
 * 判断文件是否为媒体文件（音频或视频）
 */
export const isMediaFile = (file: File): boolean => {
  return isAudioFile(file) || isVideoFile(file);
};

/**
 * 检查文件扩展名是否在指定列表中
 * @param file - 文件对象
 * @param extensions - 扩展名列表（如 ['.mp3', '.wav']）
 * @returns 是否匹配
 */
export const hasFileExtension = (file: File, extensions: string[]): boolean => {
  if (!file || !file.name) return false;
  const extension = getFileExtension(file.name);
  return extensions.some((ext) => ext.toLowerCase() === extension);
};

/**
 * 检查文件 MIME type 是否匹配指定的前缀
 * @param file - 文件对象
 * @param prefix - MIME type 前缀（如 'audio/', 'video/', 'image/'）
 * @returns 是否匹配
 */
export const hasMimeTypePrefix = (file: File, prefix: string): boolean => {
  return Boolean(
    file?.type && typeof file.type === 'string' && file.type.startsWith(prefix),
  );
};
